import { create } from 'zustand';
import { subDays, startOfDay, endOfDay } from 'date-fns';
import type { DatePreset } from '@/types/date';
import type { SavedReport, AnalyticsPageType, TableRowFilter } from '../types/analytics';

interface AnalyticsFilterState {
  /** Currently active analytics page */
  pageType: AnalyticsPageType;
  /** Date range start */
  dateFrom: Date;
  /** Date range end */
  dateTo: Date;
  /** Active date preset */
  datePreset: DatePreset;
  /** Asset tag filters (Proof of Play only) */
  assetTags: string[];
  /** Screen tag filters (Proof of Play only) */
  screenTags: string[];
  /**
   * Client-side row filter (non-tag columns — Asset Name, Play Count, Duration, …).
   * Mutually exclusive with `assetTags` / `screenTags` per the "one filter at a time"
   * invariant. Held in the store (not local dashboard state) so save/load can
   * round-trip it alongside tag filters.
   */
  rowFilter: TableRowFilter | null;
  /** Currently loaded saved report (null = default/unsaved) */
  activeReport: SavedReport | null;
  /** Whether there are unsaved changes to the active report */
  hasUnsavedChanges: boolean;
  /** Whether the tag filter panel is open */
  isFilterPanelOpen: boolean;

  setPageType: (pageType: AnalyticsPageType) => void;
  setDateRange: (from: Date, to: Date, preset: DatePreset) => void;
  setAssetTags: (tags: string[]) => void;
  setScreenTags: (tags: string[]) => void;
  setRowFilter: (filter: TableRowFilter | null) => void;
  setActiveReport: (report: SavedReport | null) => void;
  markUnsavedChanges: () => void;
  clearUnsavedChanges: () => void;
  toggleFilterPanel: () => void;
  clearFilters: () => void;
}

function getDefaultDates() {
  return {
    dateFrom: startOfDay(subDays(new Date(), 6)),
    dateTo: endOfDay(new Date()),
    datePreset: 'last7days' as DatePreset,
  };
}

/** Order-insensitive tag-array equality — lets the tag setters skip a no-op set so they
 *  don't spuriously flag `hasUnsavedChanges` (e.g. clearing an already-empty selection). */
function sameTags(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const seen = new Set(a);
  return b.every((t) => seen.has(t));
}

export const useAnalyticsFilterStore = create<AnalyticsFilterState>((set) => ({
  ...getDefaultDates(),
  pageType: 'proofOfPlay',
  assetTags: [],
  screenTags: [],
  rowFilter: null,
  activeReport: null,
  hasUnsavedChanges: false,
  isFilterPanelOpen: false,

  setPageType: (pageType) =>
    set((s) => ({
      pageType,
      // Clear active report when switching between POP and AI Analytics
      ...(s.pageType !== pageType ? { activeReport: null, hasUnsavedChanges: false } : {}),
    })),

  setDateRange: (dateFrom, dateTo, datePreset) =>
    set({ dateFrom, dateTo, datePreset, hasUnsavedChanges: true }),

  setAssetTags: (assetTags) =>
    set((s) => (sameTags(s.assetTags, assetTags) ? {} : { assetTags, hasUnsavedChanges: true })),

  setScreenTags: (screenTags) =>
    set((s) => (sameTags(s.screenTags, screenTags) ? {} : { screenTags, hasUnsavedChanges: true })),

  setRowFilter: (rowFilter) => set({ rowFilter, hasUnsavedChanges: true }),

  setActiveReport: (activeReport) =>
    set({
      activeReport,
      hasUnsavedChanges: false,
      // Restore report's saved filters when opening
      ...(activeReport
        ? {
            dateFrom: startOfDay(new Date(activeReport.startDate)),
            dateTo: endOfDay(new Date(activeReport.endDate)),
            datePreset: 'custom' as DatePreset,
            assetTags: activeReport.assetTags,
            screenTags: activeReport.screenTags,
            rowFilter: activeReport.rowFilter,
          }
        : {}),
    }),

  markUnsavedChanges: () => set({ hasUnsavedChanges: true }),
  clearUnsavedChanges: () => set({ hasUnsavedChanges: false }),
  toggleFilterPanel: () => set((s) => ({ isFilterPanelOpen: !s.isFilterPanelOpen })),

  clearFilters: () =>
    set({
      ...getDefaultDates(),
      assetTags: [],
      screenTags: [],
      rowFilter: null,
      activeReport: null,
      hasUnsavedChanges: false,
    }),
}));
